import cn from "classnames"
import { useRef } from "react"
import { useNavigate } from "react-router-dom"

import { useDeleteSastGuidance, useUpdateSastGuidance } from "@/api/useSettings"
import { App, Flex, Text, Tooltip } from "@/atoms"
import { EVENTS } from "@/const/event.constants"
import { NAMES_ROUTES, VALUES_PARAMS } from "@/const/routes.constants"
import { KAI_TAB_KEY } from "@/const/tab.constants"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTooltip } from "@/hooks/useTooltip"
import { useTranslation } from "@/hooks/useTranslation"
import { Component, InfoCircleOutlined } from "@/icons"
import { DeleteConfirmModal, useConfirmModal } from "@/molecules/ConfirmModal"
import { CreateActionEmptyTable } from "@/molecules/EmptyTable"
import { Heading } from "@/molecules/Heading"
import { LinkButton } from "@/molecules/LinkButton"
import { useGetSastGuidance } from "@/pages/SettingsPage/application/guidance/useGuidance"
import { VerticalTabTemplate } from "@/pages/SettingsPage/components/templates/VerticalTabTemplate"
import { TabTemplate } from "@/templates/TabTemplate"
import { ReactComponent as FileKai } from "./file-kai.svg"
import { getColumnsTable } from "./getColumnsTable"

import styles from "./GuidanceKaiTab.module.scss"

import type {
  SastGuidanceAPI,
  SastGuidanceResponse
} from "@/api/useSettings.types"

export const GuidanceKaiTab = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { t: tGuidance } = useTranslation("translation", {
    keyPrefix: "settings.kai-guidance"
  })
  const { abbreviateNumber } = useNumberConvertor()
  const { SETTINGS } = NAMES_ROUTES
  const { ROOT, KAI } = SETTINGS
  const to = `/${ROOT}/${KAI_TAB_KEY}/${KAI.GUIDANCE}/${VALUES_PARAMS.NEW_ENTITY}`
  const action = tGuidance("add")

  const [{ response, isLoading }, { sastLlmDisabled }] = useGetSastGuidance()
  const { data, metadata } = response || {}

  const { message } = App.useApp()
  const updateSastGuidance = useUpdateSastGuidance(
    (
      response: SastGuidanceResponse,
      payload: { needle: string; data: Partial<SastGuidanceAPI> }
    ) => {
      const id = payload?.needle?.replace("/", "")

      if (!id) return response

      return {
        ...response,
        data: response?.data?.map((item) => {
          if (item.id === id) {
            return { ...item, ...payload.data }
          }

          return item
        })
      }
    }
  )
  const deleteSastGuidance = useDeleteSastGuidance()
  const { confirm } = useConfirmModal()

  const actions = useRef({
    onEdit: (record: Pick<SastGuidanceAPI, "id" | "name">) =>
      navigate(`/${ROOT}/${KAI.ROOT}/${KAI.GUIDANCE}/${record.id}`),
    onDelete: (record: Pick<SastGuidanceAPI, "id" | "name">) => {
      const { id, name } = record

      confirm((attr) =>
        DeleteConfirmModal({
          name,
          ANALYTIC_EVENT: EVENTS.ANALYTIC_EVENTS.GUIDANCE.DELETE,
          ANALYTIC_TAGS: { id },
          entityName: "guidance",
          onOk: () => deleteSastGuidance.mutateAsync(id),
          ...attr
        })
      )
    },
    onChangeStatus: async (
      enabled: boolean,
      record: Pick<SastGuidanceAPI, "id">
    ) => {
      const key = "update-status-guidance-kai-tab"

      try {
        message.loading({ key, content: t("general.saving"), duration: 0 })
        await updateSastGuidance.mutateAsync({
          needle: `/${record.id}`,
          data: { enabled }
        })
        message.success({ key, content: t("general.saved") })
      } catch (err: any) {
        const detail = err?.data?.detail
        const content =
          typeof detail === "string" ? detail : t("layout.error.unknown")

        message.error({ key, content })
      }
    }
  })

  useTooltip(data)

  return (
    <VerticalTabTemplate
      title={<Heading level={4} title={tGuidance("title")} />}
      button={
        <Tooltip
          open={sastLlmDisabled ? undefined : false}
          title={tGuidance("disabled.add")}
        >
          <span>
            <LinkButton to={to} type="primary" disabled={sastLlmDisabled}>
              {action}
            </LinkButton>
          </span>
        </Tooltip>
      }
    >
      <TabTemplate
        tabPosition="left"
        classes={{ row: cn(sastLlmDisabled && styles.rowDisabled) }}
        loading={isLoading}
        columns={getColumnsTable({
          abbreviateNumber,
          actions,
          t,
          updating: updateSastGuidance.isPending
        })}
        dataSource={data}
        metadata={metadata}
        onRow={(record) => ({
          onClick: () => actions.current.onEdit(record)
        })}
        locale={{
          customEmptyComponent: (
            <CreateActionEmptyTable
              tKey=""
              disabled={sastLlmDisabled}
              action={action}
              message={
                <Flex
                  vertical
                  gap={16}
                  align="center"
                  className={styles.emptyMessage}
                >
                  <Flex vertical>
                    <Text type="secondary" className={styles.emptyMessageTitle}>
                      {tGuidance("description1")}
                    </Text>
                    <Text type="secondary">{tGuidance("description2")}</Text>
                  </Flex>
                  {sastLlmDisabled && (
                    <Flex
                      gap={4}
                      align="center"
                      className={styles.emptyMessageSubtitle}
                    >
                      <InfoCircleOutlined />
                      <Text type="secondary">
                        {tGuidance("disabled.description")}
                      </Text>
                    </Flex>
                  )}
                </Flex>
              }
              to={sastLlmDisabled ? undefined : to}
              image={
                <Component component={FileKai} className={styles.emptyIcon} />
              }
            />
          )
        }}
      />
    </VerticalTabTemplate>
  )
}
